import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
export const Categories = {
    Financial: "Financial",
    Malpractice: "Malpractice",
    Resource: "Resource",
    Strategic: "Strategic",
    Temperament: "Temperament"
};
export type CategoriesType = typeof Categories[keyof typeof Categories];
class RedFlagType extends Base implements EntityType {
    redFlagTypeId: uuid;
    appliesTo: uuid[];
    position: number;
    name: string;
    weight: number;
    categories: CategoriesType[];
    icon?: string | null;
    constructor(doc: typeof RedFlagType | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            redFlagTypeId: genUUID(),
            position: null,
            name: null,
            weight: 100.0,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "redFlagType";
        // @ts-ignore
        this.redFlagTypeId = doc._redFlagTypeId || doc.redFlagTypeId || doc.redflagtypeid || genUUID();
        // @ts-ignore
        this.appliesTo = doc._appliesTo || doc.appliesTo || doc.appliesto || [];
        // @ts-ignore
        this.position = util.coalesce(util.parseInt(doc._position), util.parseInt(doc.position), util.parseInt(doc.position), null);
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.weight = util.coalesce(util.parseFloat(doc._weight), util.parseFloat(doc.weight), util.parseFloat(doc.weight), 100.0);
        // @ts-ignore
        this.categories = doc._categories || doc.categories || [];
        // @ts-ignore
        this.icon = doc._icon || doc.icon || null;
    }
    get id(): uuid { return this.redFlagTypeId; }
    addAppliesTo(appliesTo: uuid): uuid {
        if (!appliesTo) throw new ValidationError('No nulls allowed in an array!');
        if (!this.appliesTo) this.appliesTo = [];
        this.appliesTo.push(appliesTo);
        return appliesTo;
    }
    updateAppliesTo(appliesTo: uuid) {
        if (!this.appliesTo) this.appliesTo = [];
        const index = this.appliesTo.findIndex(item => item === appliesTo);
        if (index !== -1) { this.appliesTo[index] = appliesTo; }
        else this.addAppliesTo(appliesTo);
    }
    removeAppliesTo(appliesTo: uuid) {
        if (!this.appliesTo) return;
        let foundItems = this.appliesTo?.filter(item => item === appliesTo);
        if (foundItems && foundItems.length > 0) {
            this.appliesTo = this.appliesTo.filter(item => !(item === appliesTo));
        }
    }
    abandonAppliesTo() { this.appliesTo = []; }
    static CATEGORIES_VALUES = ["Financial", "Malpractice", "Resource", "Strategic", "Temperament"];
    addCategory(category: string): string {
        if (!category) throw new ValidationError('No nulls allowed in an array!');
        if (!this.categories) this.categories = [];
        this.categories.push(category);
        return category;
    }
    updateCategory(category: string) {
        if (!this.categories) this.categories = [];
        const index = this.categories.findIndex(item => item === category);
        if (index !== -1) { this.categories[index] = category; }
        else this.addCategory(category);
    }
    removeCategory(category: string) {
        if (!this.categories) return;
        let foundItems = this.categories?.filter(item => item === category);
        if (foundItems && foundItems.length > 0) {
            this.categories = this.categories.filter(item => !(item === category));
        }
    }
    abandonCategories() { this.categories = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this redFlagType"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.redFlagTypeId; }
    static override getNonRefArrayFields(): string[] { return ["appliesTo", "categories"]; }
    static override getDefaultEntries(): RedFlagType[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'redFlagType',
    dataArea: 'Firm Settings',
    table: 'RED_FLAG_TYPE',
    comment: 'A red flag type is a configurable risk indicator definition used during client intake (leads) and active case management. It specifies the warning text, a risk weight (percentage contribution to an overall risk score), optional case-type scoping, and categories (Financial, Malpractice, Resource, Strategic, Temperament). Firms configure a library of these types; individual leads and cases record checked/boosted instances against them via the RED_FLAG table.',
    fields: {
        redFlagTypeId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this redFlagType'
        },
        appliesTo: {
            type: 'uuid[]',
            entityType: 'caseType',
            required: false,
            comment: 'The case type(s) this red flag applies to. No entry means it applies to all case types.'
        },
        position: {
            type: 'int',
            required: true,
            comment: 'position in the list (1-n)'
        },
        name: {
            type: 'string',
            required: true,
            comment: 'The text of the flag'
        },
        weight: {
            type: 'float',
            required: true,
            default: 100.0,
            comment: 'How much this warning counts toward the risk score, as a percentage (100 = normal weight)'
        },
        categories: {
            type: 'string[]',
            required: false,
            values: ['Financial', 'Malpractice', 'Resource', 'Strategic', 'Temperament'],
            comment: 'What categories does this fall under?'
        },

        // Transients
        icon: {
            type: 'string',
            transient: true,
            comment: 'Icon for this red flag'
        }
    },
    indexes: [
        {name: 'leads', fn: m => m.entityType === 'lead' && m.status !== 'D'},
        {name: 'matters', fn: m => m.entityType === 'matter' && m.status !== 'D'},
    ],
    maps: [
        {name: 'entityType', keysFn: t => t.entityType, presentFn: (t, k) => t.entityType === k},
        {name: 'appliesTo', keysFn: t => t.appliesTo, presentFn: (t, k) => t.appliesTo?.includes(k)}
    ]
});
    }
}
export default RedFlagType;
